import { z } from "zod";
import { connectorProviderSchema } from "./connectors.js";
export const inboxItemStateSchema = z.enum(["new", "dismissed", "promoted"]);
export const inboxItemDirectionSchema = z.enum(["incoming", "outgoing", "system"]);
export const sourceActionReceiptStatusSchema = z.enum(["pending", "succeeded", "failed", "reconciled"]);
export const inboxActionIdSchema = z.enum(["archive_source", "restore_source"]);
// A source-backed action the server has determined is currently available for
// an inbox item. Clients execute what the item advertises instead of encoding
// per-source rules locally.
export const inboxSourceActionSchema = z.object({
    id: inboxActionIdSchema,
    label: z.string().trim().min(1),
    // Execute this action (instead of a plain state patch) when the user marks
    // the item done.
    appliesOnDone: z.boolean().default(false),
    appliesOnRestore: z.boolean().default(false),
});
export const sourceActionReceiptSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    inboxItemId: z.string().trim().min(1).nullable(),
    channelId: z.string().trim().min(1).nullable(),
    objectTypeId: z.string().trim().min(1).nullable(),
    datasetId: z.string().trim().min(1).nullable(),
    sourceSystem: z.string().trim().min(1),
    sourceEntity: z.string().trim().min(1).nullable(),
    externalId: z.string().trim().min(1).nullable(),
    actionType: z.string().trim().min(1),
    actionLabel: z.string().trim().min(1),
    status: sourceActionReceiptStatusSchema,
    sourceUrl: z.string().trim().min(1).nullable(),
    sourceReceiptId: z.string().trim().min(1).nullable(),
    sourceThreadId: z.string().trim().min(1).nullable(),
    sourceMessageId: z.string().trim().min(1).nullable(),
    subject: z.string().nullable(),
    summary: z.string().nullable(),
    errorMessage: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    sourceSessionId: z.string().trim().min(1).nullable(),
    sourceRunId: z.string().trim().min(1).nullable(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
});
export const inboxMetadataEntrySchema = z.object({
    label: z.string().trim().min(1),
    value: z.string(),
});
export const inboxFieldTypeSchema = z.enum([
    "text",
    "number",
    "currency",
    "date",
    "datetime",
    "boolean",
    "url",
]);
export const inboxFieldValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
]).nullable();
export const inboxFieldSchema = z.object({
    key: z.string().trim().min(1).max(128),
    label: z.string().trim().min(1).max(256),
    type: inboxFieldTypeSchema.default("text"),
    value: inboxFieldValueSchema,
    href: z.string().trim().min(1).max(4096).nullable().default(null),
    sourceOwned: z.boolean().default(true),
});
export const inboxExtractedContentKindSchema = z.enum([
    "message",
    "event",
    "record",
    "note",
    "notification",
]);
export const inboxExtractedContentFormatSchema = z.enum(["plain", "markdown"]);
export const inboxExtractedContentBlockSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("paragraph"), text: z.string() }),
    z.object({ type: z.literal("section"), heading: z.string(), text: z.string() }),
    z.object({ type: z.literal("quote"), text: z.string(), attribution: z.string().nullable().default(null) }),
    z.object({ type: z.literal("list"), heading: z.string().nullable().default(null), items: z.array(z.string()) }),
]);
export const inboxExtractedContentLinkSchema = z.object({
    label: z.string().trim().min(1),
    url: z.string().trim().min(1),
    kind: z.enum(["source", "content", "attachment"]).default("content"),
});
export const inboxExtractedContentSchema = z.object({
    version: z.literal(1),
    kind: inboxExtractedContentKindSchema,
    format: inboxExtractedContentFormatSchema,
    agentText: z.string(),
    blocks: z.array(inboxExtractedContentBlockSchema).default([]),
    links: z.array(inboxExtractedContentLinkSchema).default([]),
    quality: z.object({
        truncated: z.boolean().default(false),
        fallback: z.boolean().default(false),
        warnings: z.array(z.string()).default([]),
    }),
    parser: z.object({
        name: z.string().trim().min(1),
        version: z.string().trim().min(1),
    }),
});
export const inboxItemSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    source: connectorProviderSchema,
    entity: z.string().trim().min(1),
    externalId: z.string().trim().min(1),
    sourceNativeId: z.string().trim().min(1),
    sourceThreadId: z.string().trim().min(1),
    sender: z.string(),
    preview: z.string(),
    body: z.string(),
    bodyHtml: z.string().nullable(),
    extractedContent: inboxExtractedContentSchema.nullable().default(null),
    sourceContext: z.string(),
    mentioned: z.boolean(),
    direction: inboxItemDirectionSchema.default("incoming"),
    sourceReplyTo: z.string().nullable(),
    state: inboxItemStateSchema,
    metadata: z.array(inboxMetadataEntrySchema),
    fields: z.array(inboxFieldSchema).default([]),
    sourceUrl: z.string().trim().min(1).nullable().default(null),
    sourceFetchedAt: z.number().int().nonnegative().nullable().default(null),
    sourceUpdatedAt: z.number().int().nonnegative().nullable().default(null),
    rawEventR2Key: z.string().nullable(),
    linkedChannelId: z.string().trim().min(1).nullable(),
    linkedThreadId: z.string().trim().min(1).nullable(),
    linkedRecordId: z.string().trim().min(1).nullable(),
    linkedDatasetId: z.string().trim().min(1).nullable().default(null),
    linkedSourceSystem: z.string().trim().min(1).nullable().default(null),
    linkedExternalId: z.string().trim().min(1).nullable().default(null),
    linkReason: z.string().nullable(),
    sourceActionReceipts: z.array(sourceActionReceiptSchema).default([]),
    availableActions: z.array(inboxSourceActionSchema).default([]),
    suggestedChannelId: z.string().trim().min(1).nullable().default(null),
    receivedAt: z.number().int().nonnegative(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    dismissedAt: z.number().int().nonnegative().nullable(),
    promotedAt: z.number().int().nonnegative().nullable(),
    /** The shared mailbox (info@) the item came from; null for personal and organisation items. */
    sharedMailboxAddress: z.string().trim().min(1).nullable().default(null),
});
export const listInboxQuerySchema = z.object({
    organisationId: z.string().trim().min(1).max(256).optional(),
    state: inboxItemStateSchema.optional(),
    bucket: z.enum(["active", "mentions", "done"]).optional(),
    direction: inboxItemDirectionSchema.optional(),
    source: connectorProviderSchema.optional(),
    linkedChannelId: z.string().trim().min(1).max(256).optional(),
    query: z.string().trim().max(200).optional(),
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(250),
});
export const listInboxResponseSchema = z.object({
    inbox: z.array(inboxItemSchema),
    nextCursor: z.string().nullable().default(null),
    hasMore: z.boolean().default(false),
});
export const patchInboxItemRequestSchema = z.object({
    state: inboxItemStateSchema.optional(),
    linkedChannelId: z.string().trim().min(1).max(256).nullable().optional(),
    linkedThreadId: z.string().trim().min(1).max(256).nullable().optional(),
    linkedRecordId: z.string().trim().min(1).max(256).nullable().optional(),
    linkedDatasetId: z.string().trim().min(1).max(256).nullable().optional(),
    linkedSourceSystem: z.string().trim().min(1).max(256).nullable().optional(),
    linkedExternalId: z.string().trim().min(1).max(1024).nullable().optional(),
    linkReason: z.string().trim().max(256).nullable().optional(),
}).strict();
export const executeInboxItemActionRequestSchema = z.object({
    actionId: inboxActionIdSchema,
}).strict();
export const createSourceActionReceiptRequestSchema = z.object({
    channelId: z.string().trim().min(1).max(256).nullable().optional(),
    objectTypeId: z.string().trim().min(1).max(256).nullable().optional(),
    datasetId: z.string().trim().min(1).max(256).nullable().optional(),
    sourceSystem: z.string().trim().min(1).max(256),
    sourceEntity: z.string().trim().min(1).max(256).nullable().optional(),
    externalId: z.string().trim().min(1).max(1024).nullable().optional(),
    actionType: z.string().trim().min(1).max(128),
    actionLabel: z.string().trim().min(1).max(256).optional(),
    status: sourceActionReceiptStatusSchema.default("succeeded"),
    sourceUrl: z.string().trim().min(1).max(4096).nullable().optional(),
    sourceReceiptId: z.string().trim().min(1).max(1024).nullable().optional(),
    sourceThreadId: z.string().trim().min(1).max(1024).nullable().optional(),
    sourceMessageId: z.string().trim().min(1).max(1024).nullable().optional(),
    subject: z.string().trim().max(1000).nullable().optional(),
    summary: z.string().trim().max(4000).nullable().optional(),
    errorMessage: z.string().trim().max(4000).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    sourceSessionId: z.string().trim().min(1).max(256).nullable().optional(),
    sourceRunId: z.string().trim().min(1).max(256).nullable().optional(),
}).strict();
export const inboxItemResponseSchema = z.object({
    inboxItem: inboxItemSchema,
    threadItems: z.array(inboxItemSchema).optional(),
});
export const sourceActionReceiptResponseSchema = z.object({
    receipt: sourceActionReceiptSchema,
    inboxItem: inboxItemSchema,
});
export const executeInboxItemActionResponseSchema = z.object({
    actionId: inboxActionIdSchema,
    receipt: sourceActionReceiptSchema,
    inboxItem: inboxItemSchema,
});
// Per-source sync health for the inbox surface — how fresh MLX's view of each
// connected source is, and whether its last sync failed. Deliberately a narrow
// projection of connector integrations: no config or secret-adjacent fields.
export const inboxSourceHealthSchema = z.object({
    provider: z.string().trim().min(1),
    status: z.string().trim().min(1),
    enabled: z.boolean(),
    lastSyncStartedAt: z.number().int().nonnegative().nullable().default(null),
    lastSyncFinishedAt: z.number().int().nonnegative().nullable().default(null),
    lastSuccessfulSyncAt: z.number().int().nonnegative().nullable().default(null),
    lastError: z.string().nullable().default(null),
});
export const listInboxSourcesResponseSchema = z.object({
    sources: z.array(inboxSourceHealthSchema),
});
// The canonical definition of "the same conversation" for inbox rows — used to
// group sibling rows for display and to dedupe source actions to one call per
// conversation. Every client must use this (the Swift mirror lives in
// MLXiOSFeature.inboxConversationKey; the macOS engine's conversationScope SQL
// encodes the same rules) so surfaces never disagree about which rows leave
// together. Slack groups by channel (the stable prefix of sourceNativeId);
// everything else groups by thread id, falling back to the native id, then to
// the row itself.
export function inboxConversationKey(item) {
    if (item.source === "slack") {
        const nativeId = item.sourceNativeId ?? "";
        const separator = nativeId.indexOf(":");
        if (separator > 0)
            return `slack:channel:${nativeId.slice(0, separator)}`;
    }
    const threadKey = item.sourceThreadId?.trim() || item.sourceNativeId?.trim();
    return threadKey ? `${item.source}:thread:${threadKey}` : `item:${item.id}`;
}
export const inboxRoutingRuleSchema = z.object({
    id: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    channelId: z.string().trim().min(1),
    matchSource: connectorProviderSchema.nullable(),
    matchSender: z.string().nullable(),
    matchKeyword: z.string().nullable(),
    enabled: z.boolean(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
});
export const listInboxRoutingRulesQuerySchema = z.object({
    organisationId: z.string().trim().min(1).max(256).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(250),
});
export const listInboxRoutingRulesResponseSchema = z.object({
    rules: z.array(inboxRoutingRuleSchema),
});
export const createInboxRoutingRuleRequestSchema = z.object({
    organisationId: z.string().trim().min(1).max(256).optional(),
    channelId: z.string().trim().min(1).max(256),
    matchSource: connectorProviderSchema.nullable().optional(),
    matchSender: z.string().trim().min(1).max(500).nullable().optional(),
    matchKeyword: z.string().trim().min(1).max(500).nullable().optional(),
}).strict().refine((input) => Boolean(input.matchSource || input.matchSender || input.matchKeyword), "At least one match condition is required.");
export const patchInboxRoutingRuleRequestSchema = z.object({
    enabled: z.boolean().optional(),
}).strict().refine((input) => input.enabled !== undefined, "At least one routing rule field is required.");
export const inboxRoutingRuleResponseSchema = z.object({
    rule: inboxRoutingRuleSchema,
});
